/**
 * InsightIQ Chat Integration Component
 * 
 * React wrapper that integrates the InsightIQ chat web component as a floating overlay.
 * The InsightIQ chat handles its own positioning, state, and interactions.
 * 
 * @module InsightIQChat
 */

import React, { useEffect, useRef, useContext, useState, useImperativeHandle, forwardRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import './style.css';

/**
 * TypeScript interface for the Insight IQ Chat custom element
 */
interface InsightIQChatElement extends HTMLElement {
  open(): Promise<void>;
  close(): Promise<void>;
  startNewChat(): Promise<void>;
  toggleFullscreen(): Promise<void>;
  setLauncherHidden(): Promise<void>;
  setLauncherVisible(): Promise<void>;
  isReady(): Promise<boolean>;
}

/**
 * Interface for InsightIQ chat methods exposed to parent components
 */
export interface InsightIQChatMethods {
  openChat(): Promise<void>;
  closeChat(): Promise<void>;
  startNewChat(): Promise<void>;
  isReady(): boolean;
}

/**
 * Extend JSX to recognize our custom element with proper typing
 */
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'insight-iq-chat': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>, 
        HTMLElement
      > & {
        'api-base'?: string;
        'theme'?: 'light' | 'dark' | 'auto';
        'launcher-text'?: string;
        'max-history'?: string;
        'size'?: 'small' | 'medium' | 'large' | 'auto';
        'allow-fullscreen'?: string;
        'show-launcher'?: string;
      };
    }
  }
}

const InsightIQChat = forwardRef<InsightIQChatMethods>((_, ref) => {
  const chatRef = useRef<InsightIQChatElement>(null);
  const { userContext } = useContext(AuthContext) || {};
  const [isComponentReady, setIsComponentReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  /**
   * Expose chat methods to parent components
   */
  useImperativeHandle(ref, () => ({
    openChat: async () => {
      if (chatRef.current && isComponentReady) {
        try {
          console.log('InsightIQ Chat: Opening chat...');
          await chatRef.current.open();
          
          // If this is the first time opening, start a new chat to ensure proper initialization
          if (!hasBeenOpened) {
            console.log('InsightIQ Chat: First open detected, starting new chat for proper initialization...');
            setTimeout(async () => {
              try {
                await chatRef.current?.startNewChat();
                setHasBeenOpened(true);
                console.log('InsightIQ Chat: New chat started, input should be ready');
              } catch (error) {
                console.error('InsightIQ Chat: Failed to start new chat:', error);
              }
            }, 500); // Small delay to ensure chat is fully opened
          }
        } catch (error) {
          console.error('InsightIQ Chat: Failed to open chat:', error);
        }
      }
    },
    closeChat: async () => {
      if (chatRef.current && isComponentReady) {
        try {
          await chatRef.current.close();
        } catch (error) {
          console.error('InsightIQ Chat: Failed to close chat:', error);
        }
      }
    },
    startNewChat: async () => {
      if (chatRef.current && isComponentReady) {
        try {
          await chatRef.current.startNewChat();
          setHasBeenOpened(true);
          console.log('InsightIQ Chat: New chat started manually');
        } catch (error) {
          console.error('InsightIQ Chat: Failed to start new chat:', error);
        }
      }
    },
    isReady: () => isComponentReady
  }), [isComponentReady, hasBeenOpened]);

  /**
   * Initialize the InsightIQ chat component with comprehensive error handling
   */
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const initializeChat = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        console.log('InsightIQ Chat: Starting initialization...');
        
        timeoutId = setTimeout(() => {
          setHasError(true);
          setIsLoading(false);
          console.error('InsightIQ Chat: Initialization timeout');
        }, 10000);
        
        const { defineCustomElements } = await import('@walmart/insight-iq-chat/loader');
        await defineCustomElements();
        await customElements.whenDefined('insight-iq-chat');
        
        clearTimeout(timeoutId);
        setIsLoading(false);
        console.log('InsightIQ Chat: Custom elements defined, component can now render');
      } catch (error) {
        clearTimeout(timeoutId);
        setHasError(true);
        setIsLoading(false);
        console.error('InsightIQ Chat: Failed to initialize InsightIQ chat:', error);
      }
    };
    initializeChat();
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  /**
   * Register event listener after the element is created in the DOM
   */
  useEffect(() => {
    if (!chatRef.current || isComponentReady) return;
    
    console.log('InsightIQ Chat: Registering event listeners...');
    
    const handleReady = async (event: any) => {
      console.log('InsightIQ Chat: Received insight:chat-ready event', event?.detail);
      try {
        const ready = await chatRef.current?.isReady();
        setIsComponentReady(!!ready);
        console.log('InsightIQ Chat: Component ready status:', ready);
      } catch (error) {
        console.error('InsightIQ Chat: Error checking ready status:', error);
      }
    };
    
    const handleError = (event: any) => {
      console.error('InsightIQ Chat: Received error event', event?.detail);
      setHasError(true);
    };
    
    const handleOpened = (event: any) => {
      console.log('InsightIQ Chat: Chat opened', event?.detail);
    };
    
    const handleClosed = (event: any) => {
      console.log('InsightIQ Chat: Chat closed', event?.detail);
    };
    
    chatRef.current.addEventListener('insight:chat-ready', handleReady);
    chatRef.current.addEventListener('insight:chat-error', handleError);
    chatRef.current.addEventListener('insight:chat-opened', handleOpened);
    chatRef.current.addEventListener('insight:chat-closed', handleClosed);
    
    // Also try to check if it's already ready
    if (chatRef.current.isReady) {
      chatRef.current.isReady().then(ready => {
        if (ready) {
          console.log('InsightIQ Chat: Component was already ready');
          setIsComponentReady(true);
        }
      }).catch(err => {
        console.log('InsightIQ Chat: Ready check failed, waiting for event:', err);
      });
    }
    
    return () => {
      chatRef.current?.removeEventListener('insight:chat-ready', handleReady);
      chatRef.current?.removeEventListener('insight:chat-error', handleError);
      chatRef.current?.removeEventListener('insight:chat-opened', handleOpened);
      chatRef.current?.removeEventListener('insight:chat-closed', handleClosed);
    };
  }, [isLoading, hasError, isComponentReady]);

  // Don't render if user is not authenticated
  if (!userContext) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="chat-widget-loading">
        Loading chat...
      </div>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <div className="chat-widget-error">
        Chat unavailable
      </div>
    );
  }

  // Render the InsightIQ chat component (always render when not loading/error)
  return (
    <div className="chat-widget-container">
      {React.createElement('insight-iq-chat', {
        ref: chatRef,
        'api-base': `${window.location.origin}/api/insight-iq`,
        'theme': 'light',
        'size': 'large',
        'show-launcher': 'false'
      })}
    </div>
  );
});

InsightIQChat.displayName = 'InsightIQChat';

export default InsightIQChat;